import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { AuthLayout } from '../components/AuthLayout';

export function VerifyEmailPage() {
  const { t } = useTranslation('auth');
  const { confirmSignUp, resendCode } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email') ?? '';

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resent, setResent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await confirmSignUp(emailParam, code);
      // Auto sign-in is handled by Cognito when autoSignIn is enabled;
      // navigate to dashboard
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.message || t('verifyEmail.failedDefault'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendCode(emailParam);
      setResent(true);
      setTimeout(() => setResent(false), 5000);
    } catch (err: any) {
      setError(err.message || t('verifyEmail.failedResend'));
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold text-gray-900">{t('verifyEmail.title')}</h1>
      <p className="mt-2 text-sm text-gray-500">
        {t('verifyEmail.subtitle')}{' '}
        <span className="font-medium text-gray-700">{emailParam || t('verifyEmail.yourEmail')}</span>
      </p>

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {resent && (
        <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
          {t('verifyEmail.resent')}
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-700">
            {t('verifyEmail.codeLabel')}
          </label>
          <input
            id="code"
            type="text"
            required
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-center tracking-[0.3em] shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            placeholder={t('verifyEmail.codePlaceholder')}
          />
        </div>

        <button
          type="submit"
          disabled={submitting || code.length < 6}
          className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          {submitting ? t('verifyEmail.submitting') : t('verifyEmail.submit')}
        </button>
      </form>

      <p className="mt-4 text-center text-sm text-gray-500">
        {t('verifyEmail.noCode')}{' '}
        <button onClick={handleResend} className="font-semibold text-primary-600 hover:text-primary-500">
          {t('verifyEmail.resendCode')}
        </button>
      </p>
    </AuthLayout>
  );
}
